/**
 * Client-info paste parser — shared between ASB and FTC Builder.
 *
 * Accepts multi-line pasted text and returns a structured client record.
 * The parser is intentionally forgiving so the user can paste an intake
 * form, an email signature block, a note-taking dump, etc.
 *
 * Supported formats (any order, any subset present):
 *   FORMAT A (7 lines): Name, DOB, SSN, Address, City State Zip, Phone, Email
 *   FORMAT B (9 lines): Name, DOB, SSN, Address, City, State, Zip, Phone, Email
 *
 * Missing pieces are returned as empty strings — the caller decides what
 * to require.
 */

const US_STATE_NAME_TO_ABBR = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'district of columbia': 'DC', 'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI',
  'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME',
  'maryland': 'MD', 'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN',
  'mississippi': 'MS', 'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE',
  'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
  'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX',
  'utah': 'UT', 'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA',
  'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
  'puerto rico': 'PR',
};
const ALL_STATE_ABBRS = new Set(Object.values(US_STATE_NAME_TO_ABBR));

export const extractCityStateZip = (raw) => {
  if (!raw) return { city: '', state: '', zip: '' };
  let s = String(raw).replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
  let zip = '';
  const zipMatch = s.match(/\b(\d{5}(?:-\d{4})?)\s*$/);
  if (zipMatch) {
    zip = zipMatch[1];
    s = s.slice(0, zipMatch.index).trim();
  }
  let state = '';
  let city = s;
  const abbrMatch = s.match(/\b([A-Za-z]{2})\s*$/);
  if (abbrMatch && ALL_STATE_ABBRS.has(abbrMatch[1].toUpperCase())) {
    state = abbrMatch[1].toUpperCase();
    city = s.slice(0, abbrMatch.index).trim();
  } else {
    const stateNames = Object.keys(US_STATE_NAME_TO_ABBR).sort((a, b) => b.length - a.length);
    const lower = s.toLowerCase();
    for (const name of stateNames) {
      if (lower.endsWith(name)) {
        state = US_STATE_NAME_TO_ABBR[name];
        city = s.slice(0, s.length - name.length).trim();
        break;
      }
    }
  }
  return { city: city || '', state: state || '', zip: zip || '' };
};

/**
 * Full parser used by FTC Builder — returns FTC's field names.
 * { fullName, dateOfBirth, ssn, address, city, state, zipCode, phone, email }
 */
export const parseClientInfoFull = (text) => {
  const lines = String(text || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const parsed = {
    fullName: lines[0] || '',
    dateOfBirth: lines[1] || '',
    ssn: lines[2] || '',
    address: lines[3] || '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
  };
  if (lines.length >= 5) {
    const line5 = lines[4] || '';
    const combined = extractCityStateZip(line5);
    const line6 = lines[5] || '';
    const isLine6Phone = /^[\d()\-.\s]+$/.test(line6) && line6.replace(/\D/g, '').length >= 10;
    const isLine6Email = line6.includes('@');
    if (combined.state && (lines.length <= 7 || isLine6Phone || isLine6Email)) {
      parsed.city = combined.city;
      parsed.state = combined.state;
      parsed.zipCode = combined.zip;
      parsed.phone = isLine6Phone ? line6 : '';
      parsed.email = isLine6Email ? line6 : (lines[6] || '');
      if (!parsed.phone && lines[6] && !lines[6].includes('@')) {
        parsed.phone = lines[6];
      }
    } else if (lines.length >= 9) {
      parsed.city = lines[4] || '';
      const rawState = (lines[5] || '').trim();
      if (/^[A-Za-z]{2}$/.test(rawState) && ALL_STATE_ABBRS.has(rawState.toUpperCase())) {
        parsed.state = rawState.toUpperCase();
      } else if (US_STATE_NAME_TO_ABBR[rawState.toLowerCase()]) {
        parsed.state = US_STATE_NAME_TO_ABBR[rawState.toLowerCase()];
      } else {
        parsed.state = rawState.toUpperCase();
      }
      parsed.zipCode = lines[6] || '';
      parsed.phone = lines[7] || '';
      parsed.email = lines[8] || '';
    } else {
      parsed.city = combined.city || line5;
      parsed.state = combined.state;
      parsed.zipCode = combined.zip;
      parsed.phone = isLine6Phone ? line6 : '';
      parsed.email = isLine6Email ? line6 : (lines[6] || '');
      if (!parsed.phone && lines[6] && !lines[6].includes('@')) {
        parsed.phone = lines[6];
      }
    }
  }
  return parsed;
};

/**
 * ASB-shaped adapter — returns the shape the AccountSummaryBreakdownPage
 * client-info form uses: { name, address1, address2, phone, email }.
 */
export const parseClientInfoForAsb = (text) => {
  const p = parseClientInfoFull(text);
  const address2 = [p.city, p.state].filter(Boolean).join(', ');
  const address2WithZip = address2 && p.zipCode ? `${address2} ${p.zipCode}` : (address2 || p.zipCode);
  return {
    name: p.fullName,
    address1: p.address,
    address2: address2WithZip,
    phone: p.phone,
    email: p.email,
  };
};
